import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, ChevronRight, FileText, ShieldCheck, Loader2, Download, LogOut, ArrowLeft, Clock } from "lucide-react";

function usePortalGuard() {
  const nav = useNavigate();
  useEffect(() => { if (!localStorage.getItem("simo_token")) nav("/login"); }, [nav]);
}

function Topbar() {
  const nav = useNavigate();
  return (
    <div className="border-b border-white/10 bg-[#0E1017]">
      <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/portal" className="font-serif text-lg text-paper tracking-wide">SIMO <span className="text-heraldic">UNIVERSITY</span> <span className="text-paper/40 text-sm">· Parent Portal</span></Link>
        <button onClick={() => { localStorage.removeItem("simo_token"); nav("/portal/login"); }} data-testid="portal-logout" className="text-paper/60 hover:text-paper text-sm inline-flex items-center gap-1.5"><LogOut className="h-4 w-4" /> Log out</button>
      </div>
    </div>
  );
}

const Shell = ({ children }) => <div className="min-h-screen bg-[#0a0b0f]"><Topbar /><div className="max-w-5xl mx-auto px-6 py-10">{children}</div></div>;

export function PortalHome() {
  usePortalGuard();
  const [data, setData] = useState(null);
  const [apps, setApps] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/portal/children").then((r) => setData(r.data)).catch((e) => {
      if (e.response?.status === 401) return;
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    });
    api.get("/portal/applications").then((r) => setApps(r.data.applications || [])).catch(() => {});
  }, []);
  return (
    <Shell>
      <h1 className="font-serif text-4xl text-paper">Welcome{data?.account?.name ? `, ${data.account.name}` : ""}</h1>
      <p className="text-paper/50 mt-2">Your applications, academic records and Intellectual Trajectory reports.</p>
      {err && <div className="mt-6 text-amber-400 text-sm" data-testid="portal-home-error">{err}</div>}

      {apps && apps.length > 0 && (
        <div className="mt-8" data-testid="portal-applications">
          <h2 className="text-paper/70 text-sm uppercase tracking-widest mb-3">Your applications</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {apps.map((a) => (
              <div key={a.application_id} data-testid={`portal-app-${a.application_id}`} className="border border-white/10 rounded-md p-5 bg-white/[0.03]">
                <div className="flex items-center justify-between">
                  <div className="text-paper font-medium">{a.applicant_name}</div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-paper/70">{a.stage}</span>
                </div>
                <div className="text-paper/50 text-sm mt-1">{a.programme}{a.plan ? ` · ${a.plan}` : ""}</div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${a.application_fee_paid ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid={`portal-app-fee-${a.application_id}`}>
                    €97 application fee: {a.application_fee_paid ? "Paid ✓" : "Not paid"}
                  </span>
                </div>
                <div className="text-paper/40 text-xs mt-2">{a.next_action}</div>
                {!a.application_fee_paid && <Link to={`/apply/${a.application_id}`} className="mt-3 inline-block text-heraldic text-xs underline" data-testid={`portal-app-continue-${a.application_id}`}>Continue / pay the €97 fee →</Link>}
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-paper/70 text-sm uppercase tracking-widest mt-10 mb-3">Students</h2>
      <div className="grid sm:grid-cols-2 gap-4" data-testid="portal-children">
        {(data?.children || []).map((c) => (
          <Link key={c.cid} to={`/portal/child/${c.cid}`} data-testid={`portal-child-${c.cid}`} className="group border border-white/10 rounded-md p-5 bg-white/[0.03] hover:border-heraldic/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-heraldic/15 text-heraldic flex items-center justify-center"><GraduationCap className="h-5 w-5" /></div>
                <div>
                  <div className="text-paper font-medium">Professor {c.name}</div>
                  <div className="text-paper/40 text-xs">{c.programme || c.cohort || "Student"}</div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-paper/30 group-hover:text-heraldic" />
            </div>
          </Link>
        ))}
        {data && data.children.length === 0 && <div className="text-paper/40 text-sm col-span-2">No students are linked to your account yet. Please contact the SIMO Registrar.</div>}
      </div>
    </Shell>
  );
}

export function ChildRecord() {
  usePortalGuard();
  const { cid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/portal/child/${cid}`).then((r) => setData(r.data)).catch((e) => { if (e.response?.status !== 401) setErr(formatApiErrorDetail(e.response?.data?.detail)); });
  }, [cid]);
  return (
    <Shell>
      <Link to="/portal" className="text-paper/50 text-sm inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="h-4 w-4" /> All children</Link>
      {err && <div className="text-amber-400 text-sm" data-testid="child-record-error">{err}</div>}
      {data && (
        <>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-heraldic/15 text-heraldic flex items-center justify-center"><GraduationCap className="h-6 w-6" /></div>
            <div>
              <h1 className="font-serif text-3xl text-paper">Professor {data.student.name}</h1>
              <p className="text-paper/40 text-sm">{data.student.programme || data.student.cohort || "Student"}</p>
            </div>
          </div>
          <h2 className="font-serif text-xl text-paper mt-10 mb-1">Academic Record — Intellectual Trajectory</h2>
          <p className="text-paper/40 text-sm mb-4">Every published report is preserved so development can be followed over time.</p>
          <div className="space-y-3" data-testid="child-reports">
            {data.reports.length === 0 && <div className="text-paper/40 text-sm">No published reports yet.</div>}
            {data.reports.map((r) => (
              <Link key={r.aid} to={`/portal/child/${cid}/report/${r.aid}`} data-testid={`portal-report-${r.aid}`} className="flex items-center justify-between border border-white/10 rounded-md p-4 bg-white/[0.03] hover:border-heraldic/50 transition-colors">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-heraldic" />
                  <div>
                    <div className="text-paper text-sm">Intellectual Trajectory · {r.period} {r.academic_year && `· ${r.academic_year}`}</div>
                    <div className="text-paper/40 text-xs">{r.programme} · Published {(r.published_at || "").slice(0, 10)}{r.first_viewed_at ? " · Viewed" : " · New"}</div>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-paper/30" />
              </Link>
            ))}
          </div>
        </>
      )}
    </Shell>
  );
}

export function ReportViewer() {
  usePortalGuard();
  const { cid, aid } = useParams();
  const [stage, setStage] = useState("gate"); // gate | code | view
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [html, setHtml] = useState("");
  const [sentTo, setSentTo] = useState("");

  const requestCode = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.post(`/portal/child/${cid}/report/${aid}/request-code`); setSentTo(data.sent_to); setStage("code"); toast.success("Code sent to your email"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  }, [cid, aid]);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post(`/portal/child/${cid}/report/${aid}/verify`, { code }); setHtml(data.html); setStage("view"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const download = async () => {
    try {
      const res = await api.get(`/portal/child/${cid}/report/${aid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const el = document.createElement("a"); el.href = url; el.download = "SIMO_Intellectual_Trajectory.pdf"; el.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Shell>
      <Link to={`/portal/child/${cid}`} className="text-paper/50 text-sm inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="h-4 w-4" /> Academic record</Link>
      {stage !== "view" && (
        <div className="max-w-md" data-testid="report-gate">
          <div className="flex items-center gap-2 text-heraldic mb-2"><ShieldCheck className="h-5 w-5" /><span className="text-xs uppercase tracking-widest">Confidential report</span></div>
          <h1 className="font-serif text-3xl text-paper">Confirm your identity</h1>
          <p className="text-paper/50 text-sm mt-2">This report is confidential. We'll email you a one-time code to confirm it's you before it opens.</p>
          {stage === "gate" && <button onClick={requestCode} disabled={busy} data-testid="report-request-code" className="mt-6 bg-heraldic text-white rounded-sm px-5 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Email me a code</button>}
          {stage === "code" && (
            <form onSubmit={verify} className="mt-6 space-y-3">
              <p className="text-paper/40 text-xs inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Sent to {sentTo}. Expires in 10 minutes.</p>
              <input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="6-digit code" data-testid="report-code-input" className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-3 text-lg tracking-[0.4em] text-center text-paper" />
              <button disabled={busy} data-testid="report-verify" className="w-full bg-heraldic text-white rounded-sm py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} View report</button>
              <button type="button" onClick={requestCode} className="text-heraldic underline text-xs">Resend code</button>
            </form>
          )}
        </div>
      )}
      {stage === "view" && (
        <div data-testid="report-view">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-serif text-2xl text-paper">Intellectual Trajectory Report</h1>
            <button onClick={download} data-testid="report-download" className="bg-heraldic text-white rounded-sm px-4 py-2 text-sm inline-flex items-center gap-2"><Download className="h-4 w-4" /> Download PDF</button>
          </div>
          <div className="border border-white/10 rounded-md overflow-hidden bg-white">
            <iframe title="report" sandbox="allow-same-origin allow-popups allow-downloads" srcDoc={html || "<p style='padding:24px;font-family:sans-serif'>Rapport indisponible. Merci de réessayer ou de nous contacter.</p>"} className="w-full" style={{ height: "80vh", border: 0 }} />
          </div>
        </div>
      )}
    </Shell>
  );
}
